import React from 'react';
import { ZijMenu } from './ZijMenu';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  CreditCard,
  SlidersHorizontal,
  Coins,
  FolderOpen,
  Bell,
  Siren,
  Flame,
} from 'lucide-react';

/**
 * Mijn Belagom = de bedrijfsmatige compliance-backoffice van de zzp'er.
 * Alles wat loondienst-uitstraling geeft (Belagom-mailadres, handtekening,
 * visitekaartjes) is bewust verwijderd i.v.m. de Wet DBA. Platformfeedback en
 * klantenservice leven niet meer in dit menu maar in de zwevende
 * support-widget rechtsonder (zie PlatformSupportWidget).
 */
export type BelagomView =
  | 'screening'
  | 'uitbetalingen'
  | 'portfolio'
  | 'pools'
  | 'contracten'
  | 'crisis'
  | 'vertrouwenspersoon'
  | 'notificaties';

/** Oude ?view=-waardes blijven werken zodat bestaande links niet breken. */
export const LEGACY_BELAGOM_VIEWS: Record<string, BelagomView> = {
  profiel: 'portfolio',
  documenten: 'contracten',
  email: 'portfolio',
  visitekaartjes: 'portfolio',
  verbeterpunten: 'portfolio',
  klantenservice: 'portfolio',
};

export const BELAGOM_SIDEBAR_ITEMS: Array<{
  label: string;
  view: BelagomView;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  href?: string;
}> = [
  {
    label: 'Screening & Kwalificaties',
    view: 'screening',
    icon: ShieldCheck,
    description: 'KvK, BIG/NIP en verzekeringen actueel houden',
  },
  {
    label: 'Stripe Uitbetalingen',
    view: 'uitbetalingen',
    icon: CreditCard,
    description: 'Bankkoppeling (IBAN) en uitbetalingen',
  },
  {
    label: 'Zakelijk Portfolio',
    view: 'portfolio',
    icon: SlidersHorizontal,
    description: 'Bio, video en reisradius',
  },
  {
    label: 'Mijn Pools & Tarieven',
    view: 'pools',
    icon: Coins,
    description: 'Jaarlijkse evaluatie en indexering',
  },
  {
    label: 'Contracten & Documenten',
    view: 'contracten',
    icon: FolderOpen,
    description: 'Getekende gebruikersovereenkomst en klachtenreglement',
  },
  {
    label: 'Crisisdienst & Stand-by',
    view: 'crisis',
    icon: Siren,
    description: 'Rooster, tokenwaarde en alarmen',
  },
  {
    label: 'Waakvlam & Zichtbaarheid',
    view: 'vertrouwenspersoon',
    icon: Flame,
    description: 'Vertrouwenspersoon: 12-uurs paraatheid en GPS-check-in op locatie',
  },
  {
    label: 'Notificatie-instellingen',
    view: 'notificaties',
    icon: Bell,
    description: 'E-mail, SMS en push',
  },
];

interface ProfessionalBelagomSidebarProps {
  activeView: BelagomView;
  onViewChange: (view: BelagomView) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const ProfessionalBelagomSidebar: React.FC<ProfessionalBelagomSidebarProps> = ({
  activeView,
  onViewChange,
  mobileOpen,
  onMobileClose,
}) => {
  const { t } = useTranslation('common');

  return (
    <ZijMenu
      titel={t('belagomSidebar.title', { defaultValue: 'Mijn BeLagom' })}
      ondertitel={t('belagomSidebar.subtitle', { defaultValue: 'Compliance, uitbetalingen, portfolio en instellingen' })}
      groepen={[{
        items: BELAGOM_SIDEBAR_ITEMS.map((item) => ({
          key: item.view,
          label: t(`belagomSidebar.${item.view}`, { defaultValue: item.label }),
          omschrijving: item.description,
          icon: item.icon,
          actief: activeView === item.view,
          ...(item.href ? { href: item.href } : { onClick: () => onViewChange(item.view) }),
        })),
      }]}
      mobielOpen={mobileOpen}
      onMobielSluit={onMobileClose}
    />
  );
};
